import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  getAdditionalUserInfo,
  GoogleAuthProvider,
  onAuthStateChanged,
  reload,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import { doc, getDoc, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { getEmailVerificationActionCodeSettings } from '../auth/emailVerificationSettings';
import { auth, db } from '../firebase';

export { getEmailVerificationActionCodeSettings } from '../auth/emailVerificationSettings';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const login = async ({ email, password }) => {
    const credential = await signInWithEmailAndPassword(auth, email, password);
    setUser(credential.user);
    return credential.user;
  };

  const register = async ({ email, password }) => {
    const credential = await createUserWithEmailAndPassword(auth, email, password);

    const userRef = doc(db, 'users', credential.user.uid);
    const snapshot = await getDoc(userRef);

    if (!snapshot.exists()) {
      await setDoc(userRef, {
        uid: credential.user.uid,
        email: credential.user.email,
        credits: 1,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } else {
      await updateDoc(userRef, {
        email: credential.user.email,
        updatedAt: serverTimestamp(),
      });
    }

    await sendEmailVerification(credential.user, getEmailVerificationActionCodeSettings());
    setUser(credential.user);
    return credential.user;
  };

  const resendEmailVerification = async () => {
    const current = auth.currentUser;
    if (!current) {
      throw new Error('auth/no-current-user');
    }
    await sendEmailVerification(current, getEmailVerificationActionCodeSettings());
  };

  const refreshUser = async () => {
    const current = auth.currentUser;
    if (!current) {
      throw new Error('auth/no-current-user');
    }
    await reload(current);
    setUser(auth.currentUser);
    return auth.currentUser;
  };

  const requestPasswordReset = async (email) => {
    await sendPasswordResetEmail(auth, email);
  };

  const loginWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    const credential = await signInWithPopup(auth, provider);
    const firebaseUser = credential.user;
    const isNewUser = getAdditionalUserInfo(credential)?.isNewUser === true;

    const userRef = doc(db, 'users', firebaseUser.uid);
    const snapshot = await getDoc(userRef);

    if (!snapshot.exists()) {
      await setDoc(userRef, {
        uid: firebaseUser.uid,
        displayName: firebaseUser.displayName ?? null,
        email: firebaseUser.email ?? null,
        photoURL: firebaseUser.photoURL ?? null,
        provider: 'google.com',
        credits: 1,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } else {
      const existingCredits = snapshot.data()?.credits;
      const creditsPatch =
        existingCredits == null || typeof existingCredits !== 'number' ? { credits: 0 } : {};
      await updateDoc(userRef, {
        email: firebaseUser.email ?? null,
        lastLoginAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...creditsPatch,
      });
    }

    setUser(firebaseUser);
    return { user: firebaseUser, isNewUser };
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
  };

  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated: Boolean(user),
      login,
      register,
      loginWithGoogle,
      resendEmailVerification,
      refreshUser,
      requestPasswordReset,
      logout,
    }),
    [user, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/** True when this account must verify email before using the app (email/password users only). */
export function needsEmailVerification(user) {
  if (!user || user.emailVerified) return false;
  return user.providerData.some((p) => p.providerId === 'password');
}

/** Maps Firebase Google sign-in errors to short user-facing copy. */
export function mapGoogleSignInError(err) {
  const code = err?.code;
  switch (code) {
    case 'auth/popup-closed-by-user':
      return 'Sign-in was cancelled.';
    case 'auth/popup-blocked':
      return 'The sign-in pop-up was blocked. Allow pop-ups for this site and try again.';
    case 'auth/cancelled-popup-request':
      return 'Another sign-in window is already open. Close it and try again.';
    case 'auth/account-exists-with-different-credential':
      return 'An account with this email already exists. Sign in with your email and password, then link Google in account settings if available.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection and try again.';
    case 'auth/unauthorized-domain':
      return 'This site is not allowed to sign in. Contact support if this keeps happening.';
    case 'auth/operation-not-allowed':
      return 'Google sign-in is not enabled. Please contact support.';
    default:
      return 'Unable to sign in with Google. Please try again.';
  }
}
